import {
  DEFAULT_SEARCH_FILTERS,
  SORT_COLUMNS,
  type DatabaseSearchFilters,
  type DatabaseSearchPage,
  type DatabaseSearchRequest,
  type DatabaseSearchResult,
} from '@/models/DatabaseSearch'

/**
 * The search page's state, kept in sessionStorage so leaving the page and
 * coming back restores it until the tab closes. The results are the one page
 * already on screen, so restoring costs no search.
 */
export interface SavedSearch {
  filters: DatabaseSearchFilters
  keywordDraft: string
  locationsOpen: boolean
  keywordsOpen: boolean
  searched: DatabaseSearchRequest | null
  results: DatabaseSearchPage | null
}

export const EMPTY_SEARCH: SavedSearch = {
  filters: DEFAULT_SEARCH_FILTERS,
  keywordDraft: '',
  locationsOpen: false,
  keywordsOpen: false,
  searched: null,
  results: null,
}

const SEARCH_PREFIX = 'databaseSearch:'
const SELECTIONS_PREFIX = 'databaseSelections:'

// Keyed by user and org: switching org, or another person signing in on the
// same tab, must not show someone else's search.
export function savedSearchKey(userUuid: string, orgUuid: string): string {
  return `${SEARCH_PREFIX}v1:${userUuid}:${orgUuid}`
}

// sessionStorage outlives logout until the tab closes, so logout clears it.
export function clearDatabaseSearchStorage(): void {
  try {
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i))
    for (const key of keys) {
      if (key?.startsWith(SEARCH_PREFIX) || key?.startsWith(SELECTIONS_PREFIX)) {
        sessionStorage.removeItem(key)
      }
    }
  } catch {
    // Blocked storage holds nothing to clear.
  }
}

// Storage can throw (blocked site data, private windows); the page then
// starts empty and saves nothing.
export function loadSearch(key: string): SavedSearch {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return EMPTY_SEARCH
    const saved = JSON.parse(raw) as Partial<SavedSearch>
    if (!saved || typeof saved !== 'object') return EMPTY_SEARCH
    let searched = saved.searched
      ? { ...saved.searched, filters: { ...DEFAULT_SEARCH_FILTERS, ...saved.searched.filters } }
      : null
    // The server refuses any other column, so the next page or sort would fail.
    if (searched && !(SORT_COLUMNS as readonly string[]).includes(searched.sort_by)) {
      searched = { ...searched, sort_by: 'funder', sort_dir: 'asc' }
    }
    const results = saved.results ?? null
    return {
      // A filter added since the save takes its default.
      filters: { ...DEFAULT_SEARCH_FILTERS, ...saved.filters },
      keywordDraft: saved.keywordDraft ?? '',
      locationsOpen: saved.locationsOpen ?? false,
      keywordsOpen: saved.keywordsOpen ?? false,
      // Results without the request behind them can't be paged or sorted.
      searched: searched && results ? searched : null,
      results: searched && results ? results : null,
    }
  } catch {
    return EMPTY_SEARCH
  }
}

export function saveSearch(key: string, search: SavedSearch): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(search))
  } catch {
    // Full or blocked storage only costs the restore.
  }
}

export const MAX_SELECTIONS = 20

// Ticked rows, in the order ticked. Each is a copy of its row, since the page
// only ever holds the current page of results. Kept apart from the saved
// search so Reset filters and new searches leave them alone.
export function selectionsKey(userUuid: string, orgUuid: string): string {
  return `${SELECTIONS_PREFIX}v1:${userUuid}:${orgUuid}`
}

export function loadSelections(key: string): DatabaseSearchResult[] {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) ?? '[]')
    if (!Array.isArray(saved)) return []
    return saved
      .filter((row) => typeof row?.opportunity_uuid === 'string')
      .slice(0, MAX_SELECTIONS)
  } catch {
    return []
  }
}

export function saveSelections(key: string, rows: DatabaseSearchResult[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(rows))
  } catch {
    // Full or blocked storage only costs the restore.
  }
}
